import React, { useState, useRef, useEffect } from 'react';
import axios from 'axios';
import { Document, Page, pdfjs } from 'react-pdf';
import { useNavigate, useLocation } from 'react-router-dom';
import { API_ENDPOINTS } from './config';
import logger from './utils/logger';
import { FiUploadCloud, FiSave, FiArrowLeft, FiType, FiPenTool, FiX, FiCheck } from "react-icons/fi";
import SigningLoader from './SigningLoader';
import './App.css';

pdfjs.GlobalWorkerOptions.workerSrc = `//unpkg.com/pdfjs-dist@${pdfjs.version}/build/pdf.worker.min.mjs`;

function CreateTemplate() {
    const [step, setStep] = useState(1);
    const [templateTitle, setTemplateTitle] = useState("");
    const [pdfFile, setPdfFile] = useState(null);
    const [pdfUrl, setPdfUrl] = useState('');
    const [isUploading, setIsUploading] = useState(false);
    const [numPages, setNumPages] = useState(null);
    const [pageNumber, setPageNumber] = useState(1);

    // Editing / Viewing existing template
    const [templateId, setTemplateId] = useState(null);
    const [mode, setMode] = useState("create"); // create | edit | view

    // Field creation modal state
    const [fieldModal, setFieldModal] = useState({ open: false, type: null, label: "" });

    // Save status banner
    const [saveStatus, setSaveStatus] = useState({ type: "", message: "" });

    // Template Elements
    const [roles, setRoles] = useState([]);
    const [textFields, setTextFields] = useState([]);

    // Dragging State
    const [isDragging, setIsDragging] = useState(false);
    const [dragItem, setDragItem] = useState(null); // { type: 'role'|'text', index: 0 }
    const [dragOffset, setDragOffset] = useState({ x: 0, y: 0 }); // Fix for drag sticking

    const navigate = useNavigate();
    const location = useLocation();
    const renderedPageWidth = 500;

    const handleFileUpload = (e) => {
        const file = e.target.files[0];
        if (!file) return;

        setIsUploading(true);
        const formData = new FormData();
        formData.append('file', file);
        const baseUrl = API_ENDPOINTS.LOGIN.split('/auth')[0];

        axios.post(`${baseUrl}/files/upload`, formData)
            .then(response => {
                setPdfFile(response.data.file_path);
                setPdfUrl(response.data.public_url);
                setIsUploading(false);
            })
            .catch(err => {
                console.error(err);
                setIsUploading(false);
                alert("Upload failed");
            });
    };

    // --- FIXED DRAG AND DROP LOGIC ---
    const handleMouseDown = (e, type, index) => {
        e.preventDefault();

        // Calculate the offset of the mouse within the element
        // This ensures the element doesn't "snap" to center on the mouse
        const target = e.currentTarget;
        const rect = target.getBoundingClientRect();
        const offsetX = e.clientX - rect.left;
        const offsetY = e.clientY - rect.top;

        setDragOffset({ x: offsetX, y: offsetY });
        setIsDragging(true);
        setDragItem({ type, index });
    };

    const handleMouseMove = (e) => {
        if (!isDragging || !dragItem) return;

        const container = document.getElementById("pdf-wrapper");
        if (!container) return;

        const rect = container.getBoundingClientRect();

        // Calculate new X/Y relative to container, subtracting the initial click offset
        let x = e.clientX - rect.left - dragOffset.x;
        let y = e.clientY - rect.top - dragOffset.y;

        // Boundaries
        if (x < 0) x = 0;
        if (y < 0) y = 0;
        // Optional: Constrain right edge
        // if (x > renderedPageWidth - 50) x = renderedPageWidth - 50;

        if (dragItem.type === 'role') {
            const newRoles = [...roles];
            newRoles[dragItem.index].x = x;
            newRoles[dragItem.index].y = y;
            setRoles(newRoles);
        } else {
            const newFields = [...textFields];
            newFields[dragItem.index].x = x;
            newFields[dragItem.index].y = y;
            setTextFields(newFields);
        }
    };

    const handleMouseUp = () => {
        setIsDragging(false);
        setDragItem(null);
    };

    const addRole = () => {
        setFieldModal({ open: true, type: 'role', label: '' });
    };

    const addTextField = () => {
        setFieldModal({ open: true, type: 'text', label: '' });
    };

    const deleteItem = (type, index) => {
        if (type === 'role') {
            setRoles(roles.filter((_, i) => i !== index));
        } else {
            setTextFields(textFields.filter((_, i) => i !== index));
        }
    };

    const handleSaveTemplate = async () => {
        if (!templateTitle) return alert("Please enter a title");
        if (!pdfFile) return alert("Please upload a PDF");
        if (roles.length === 0) return alert("Please add at least one signature role");

        const baseUrl = API_ENDPOINTS.LOGIN.split('/auth')[0];
        try {
            const payload = {
                title: templateTitle,
                file_path: pdfFile,
                roles: roles,
                text_fields: textFields
            };

            if (mode === "edit" && templateId) {
                await axios.put(`${baseUrl}/templates/${templateId}`, payload);
                setSaveStatus({ type: "success", message: "Template updated successfully." });
            } else {
                await axios.post(`${baseUrl}/templates/create`, payload);
                setSaveStatus({ type: "success", message: "Template created successfully." });
            }
            navigate('/dashboard?tab=templates');
        } catch (e) {
            console.error(e);
            setSaveStatus({ type: "error", message: "Failed to save template. Please try again." });
        }
    };

    // Load query params to determine mode / templateId
    useEffect(() => {
        const params = new URLSearchParams(location.search);
        const id = params.get("id");
        const m = params.get("mode");
        if (id) {
            setTemplateId(id);
        }
        if (m === "edit" || m === "view") {
            setMode(m);
        }
    }, [location.search]);

    // If editing or viewing, fetch template details
    useEffect(() => {
        const fetchTemplate = async () => {
            if (!templateId) return;
            try {
                const baseUrl = API_ENDPOINTS.LOGIN.split('/auth')[0];
                const res = await axios.get(`${baseUrl}/templates/${templateId}`);
                const t = res.data;
                setTemplateTitle(t.title || "");
                setRoles(t.roles || []);
                setTextFields(t.text_fields || []);
                setPdfFile(t.file_path);
                setPdfUrl(`${baseUrl}/files/uploads/${t.file_path}`);
                setStep(2); // Jump directly to design step when editing/viewing
            } catch (err) {
                console.error(err);
            }
        };
        if (mode !== "create") {
            fetchTemplate();
        }
    }, [templateId, mode]);

    const applyFieldModal = () => {
        const trimmed = fieldModal.label.trim();
        if (!trimmed) return;
        if (fieldModal.type === 'role') {
            setRoles([...roles, { label: trimmed, page: pageNumber, x: 50, y: 50 }]);
        } else if (fieldModal.type === 'text') {
            setTextFields([...textFields, { label: trimmed, page: pageNumber, x: 50, y: 150 }]);
        }
        setFieldModal({ open: false, type: null, label: "" });
    };

    const canEdit = mode !== "view";

    useEffect(() => {
        if (!saveStatus.message) return;
        const t = setTimeout(() => setSaveStatus({ type: "", message: "" }), 3500);
        return () => clearTimeout(t);
    }, [saveStatus]);

    const customStyles = `
    .compact-upload-zone {
      position: relative;
      border: 2px dashed var(--black-border-light);
      border-radius: 8px;
      padding: 40px;
      text-align: center;
      background: var(--black-bg-card-4);
      cursor: pointer;
      transition: all 0.2s;
      color: var(--text-on-black-muted);
    }
    .compact-upload-zone:hover {
      background: var(--black-bg-card-3);
      border-color: var(--primary);
    }
    .spinner-sm {
        width: 12px; height: 12px;
        border: 2px solid #FFD600; border-top-color: transparent;
        border-radius: 50%; display: inline-block;
        animation: rotation 1s linear infinite;
    }
    .field-modal-backdrop {
      position: fixed;
      inset: 0;
      background: rgba(15, 23, 42, 0.65);
      display: flex;
      align-items: center;
      justify-content: center;
      z-index: 200;
    }
    .field-modal {
      background: #0f172a;
      color: white;
      padding: 24px 26px 18px;
      border-radius: 16px;
      min-width: 360px;
      box-shadow: 0 25px 80px rgba(0,0,0,0.6);
    }
    .field-modal h3 {
      margin: 0 0 12px 0;
      font-size: 18px;
    }
    .field-modal p {
      margin: 0 0 16px 0;
      font-size: 13px;
      color: #e5e7eb;
    }
    .field-modal-input {
      width: 100%;
      padding: 10px 12px;
      border-radius: 8px;
      border: 1px solid #4b5563;
      background: #020617;
      color: #f9fafb;
      margin-bottom: 16px;
      font-size: 14px;
    }
    .field-modal-actions {
      display: flex;
      justify-content: flex-end;
      gap: 10px;
    }
    .template-save-toast {
      position: fixed;
      top: 82px;
      right: 24px;
      padding: 10px 14px;
      border-radius: 999px;
      font-size: 13px;
      display: flex;
      align-items: center;
      gap: 8px;
      box-shadow: 0 10px 30px rgba(15,23,42,0.25);
      z-index: 180;
    }
    .template-save-toast.success {
      background: #022c22;
      color: #bbf7d0;
      border: 1px solid #16a34a;
    }
    .template-save-toast.error {
      background: #450a0a;
      color: #fecaca;
      border: 1px solid #dc2626;
    }
    .template-placed-input {
      border: none;
      background: transparent;
      font-size: 13px;
      padding: 2px 4px;
      outline: none;
      min-width: 60px;
      color: var(--text-on-black);
    }
    .template-placed-input:focus {
      background: var(--black-bg-card-4);
      border-radius: 4px;
    }
    `;

    const renameItem = (type, index, newLabel) => {
        if (type === 'text') {
            setTextFields(prev =>
                prev.map((item, i) => (i === index ? { ...item, label: newLabel } : item))
            );
        } else {
            setRoles(prev =>
                prev.map((item, i) => (i === index ? { ...item, label: newLabel } : item))
            );
        }
    };

    return (
        <div className="App" onMouseMove={handleMouseMove} onMouseUp={handleMouseUp} style={{height: '100vh', display: 'flex', flexDirection: 'column', overflow: 'hidden'}}>
            <style>{customStyles}</style>

                <div className="create-template-header" style={{padding: '20px', borderBottom: '1px solid var(--black-border)', display:'flex', alignItems:'center', justifyContent:'space-between', background: 'var(--black-bg-topbar)', zIndex: 10}}>
                <div style={{display:'flex', alignItems:'center', gap: 15}}>
                    <button className="btn btn-secondary" onClick={() => navigate('/dashboard?tab=templates')}><FiArrowLeft /> Back</button>
                    <h2 style={{color: 'var(--text-on-black)', margin: 0}}>{mode === "create" ? "Create Template" : mode === "edit" ? "Edit Template" : "View Template"}</h2>
                </div>
                <div style={{display:'flex', gap: 10}}>
                     {step === 2 && canEdit && (
                         <button className="btn btn-primary" onClick={handleSaveTemplate}><FiSave style={{marginRight:5}}/> Save Template</button>
                     )}
                </div>
            </div>
            {saveStatus.message && (
              <div className={`template-save-toast ${saveStatus.type}`}>
                {saveStatus.message}
              </div>
            )}

            {step === 1 && mode === "create" && (
                <div style={{maxWidth: 600, margin: '40px auto', padding: 20, background: 'var(--black-bg-card-3)', borderRadius: 12, border: '1px solid var(--black-border)'}}>
                    <div className="form-group">
                        <label style={{color: 'var(--text-on-black-secondary)'}}>Template Title</label>
                        <input type="text" value={templateTitle} onChange={e => setTemplateTitle(e.target.value)} placeholder="e.g. Standard NDA" disabled={!canEdit} />
                    </div>
                    <div className="form-group">
                        <label style={{color: 'var(--text-on-black-secondary)'}}>Upload PDF</label>
                        <div className="compact-upload-zone">
                             <input type="file" accept="application/pdf" onChange={handleFileUpload} disabled={isUploading || !canEdit} style={{position: 'absolute', opacity: 0, width: '100%', height: '100%', top: 0, left: 0, cursor: 'pointer', zIndex: 10}} id="file-upload" />
                             <div style={{pointerEvents: 'none'}}>
                                 <FiUploadCloud size={32} style={{color: 'var(--text-on-black-muted)'}}/>
                                 <p style={{color: 'var(--text-on-black-muted)', margin: '5px 0', fontSize: 14}}>
                                     {pdfFile ? <span style={{color: '#10b981', fontWeight: 600}}><FiCheck /> File Uploaded</span> : "Click to select a PDF file"}
                                 </p>
                                 {isUploading && <SigningLoader size="md" label="Uploading..." />}
                             </div>
                        </div>
                    </div>
                    <button className="btn btn-primary" style={{width:'100%'}} disabled={!pdfFile || !templateTitle} onClick={() => setStep(2)}>Next: Add Placeholders</button>
                </div>
            )}

            {step === 2 && (
                <div style={{display:'flex', flex: 1, overflow: 'hidden'}}>
                    {/* SIDEBAR TOOLS */}
                    <div style={{width: 250, background: 'var(--black-bg-card-2)', borderRight: '1px solid var(--black-border)', padding: 20, overflowY:'auto'}}>
                         <h4 style={{color: 'var(--text-on-black)', marginBottom: 10}}>Tools</h4>
                         <button className="btn btn-secondary" style={{width:'100%', marginBottom: 10, justifyContent:'flex-start'}} onClick={addTextField} disabled={!canEdit}>
                             <FiType style={{marginRight: 8}}/> Add Text Field
                         </button>
                         <button className="btn btn-secondary" style={{width:'100%', marginBottom: 20, justifyContent:'flex-start'}} onClick={addRole} disabled={!canEdit}>
                             <FiPenTool style={{marginRight: 8}}/> Add Signature Block
                         </button>

                         <h4 style={{color: 'var(--text-on-black)', marginTop: 15, marginBottom: 8}}>Placed Items (Page {pageNumber})</h4>
                         <div style={{fontSize: 12, color: 'var(--text-on-black-muted)', marginBottom: 10}}>Drag items on the PDF to position them.</div>

                         {textFields.filter(t => t.page === pageNumber).map((t, idx) => (
                             <div key={`txt-${idx}`} style={{background:'var(--black-bg-card-4)', padding: 8, borderRadius: 6, border: '1px solid var(--black-border)', marginBottom: 5, display:'flex', justifyContent:'space-between', alignItems:'center'}}>
                                 <span style={{display:'flex', alignItems:'center', gap:6}}>
                                   <FiType />
                                   {canEdit ? (
                                     <input
                                       className="template-placed-input"
                                       value={t.label}
                                       onChange={e => renameItem('text', textFields.indexOf(t), e.target.value)}
                                     />
                                   ) : (
                                     <span>{t.label}</span>
                                   )}
                                 </span>
                                 {canEdit && (
                                   <button onClick={() => deleteItem('text', textFields.indexOf(t))} style={{background:'none', border:'none', color:'#ef4444', cursor:'pointer'}}><FiX/></button>
                                 )}
                             </div>
                         ))}

                         {roles.filter(r => r.page === pageNumber).map((r, idx) => (
                             <div key={`role-${idx}`} style={{background:'rgba(255, 214, 0, 0.1)', padding: 8, borderRadius: 6, border: '1px solid rgba(255, 214, 0, 0.4)', marginBottom: 5, display:'flex', justifyContent:'space-between', alignItems:'center'}}>
                                 <span style={{color: 'var(--primary)', display:'flex', alignItems:'center', gap:6}}>
                                   <FiPenTool />
                                   {canEdit ? (
                                     <input
                                       className="template-placed-input"
                                       value={r.label}
                                       onChange={e => renameItem('role', roles.indexOf(r), e.target.value)}
                                     />
                                   ) : (
                                     <span>{r.label}</span>
                                   )}
                                 </span>
                                 {canEdit && (
                                   <button onClick={() => deleteItem('role', roles.indexOf(r))} style={{background:'none', border:'none', color:'#ef4444', cursor:'pointer'}}><FiX/></button>
                                 )}
                             </div>
                         ))}
                    </div>

                    {/* PDF CANVAS */}
                    <div style={{flex: 1, background: 'var(--black-bg-card-1)', padding: 20, overflow: 'auto', display:'flex', justifyContent:'center'}}>
                         <div style={{position:'relative', width: renderedPageWidth, height: 'max-content', background:'#1a1a1a', boxShadow: '0 4px 12px rgba(0,0,0,0.5)', marginBottom: 80}} id="pdf-wrapper">
                             {pdfUrl && (
                                 <Document file={pdfUrl} onLoadSuccess={({ numPages }) => setNumPages(numPages)}>
                                     <Page pageNumber={pageNumber} width={renderedPageWidth} renderTextLayer={false} renderAnnotationLayer={false} />
                                 </Document>
                             )}

                             {textFields.filter(t => t.page === pageNumber).map((t, idx) => (
                                 <div
                                    key={idx}
                                    onMouseDown={canEdit ? (e) => handleMouseDown(e, 'text', textFields.indexOf(t)) : undefined}
                                    style={{
                                        position: 'absolute', left: t.x, top: t.y,
                                        padding: '4px 8px', background: 'rgba(255, 255, 0, 0.3)', border: '1px dashed orange',
                                        cursor: 'move', fontSize: 12, color: 'black'
                                    }}
                                 >
                                     {t.label}
                                 </div>
                             ))}

                             {roles.filter(r => r.page === pageNumber).map((r, idx) => (
                                 <div
                                    key={idx}
                                    onMouseDown={canEdit ? (e) => handleMouseDown(e, 'role', roles.indexOf(r)) : undefined}
                                    style={{
                                        position: 'absolute', left: r.x, top: r.y,
                                        width: 150, height: 50,
                                        /* Branding: Yellow background with darker border for signatures */
                                        background: 'rgba(255, 214, 0, 0.4)', border: '2px solid #F57F17',
                                        cursor: 'move', display:'flex', alignItems:'center', justifyContent:'center',
                                        color: 'black', fontWeight: 'bold', fontSize: 12
                                    }}
                                 >
                                     Sign: {r.label}
                                 </div>
                             ))}
                         </div>
                    </div>
                </div>
            )}

            {step === 2 && (
                <div style={{position: 'fixed', bottom: 20, left: '50%', transform: 'translateX(-50%)', background: 'var(--black-bg-card-3)', padding: '10px 20px', borderRadius: 50, boxShadow: '0 4px 12px rgba(0,0,0,0.5)', border: '1px solid var(--black-border)', display: 'flex', gap: 10, zIndex: 100}}>
                     <button className="btn btn-secondary btn-sm" disabled={pageNumber <= 1} onClick={() => setPageNumber(pageNumber - 1)}>Prev Page</button>
                     <span style={{display:'flex', alignItems:'center', color: 'var(--text-on-black)'}}>Page {pageNumber} of {numPages}</span>
                     <button className="btn btn-secondary btn-sm" disabled={pageNumber >= numPages} onClick={() => setPageNumber(pageNumber + 1)}>Next Page</button>
                </div>
            )}
        {fieldModal.open && (
          <div className="field-modal-backdrop" onClick={() => setFieldModal({ open: false, type: null, label: "" })}>
            <div className="field-modal" onClick={e => e.stopPropagation()}>
              <h3>{fieldModal.type === 'role' ? 'Add Signature Role' : 'Add Text Field'}</h3>
              <p>{fieldModal.type === 'role'
                ? 'Enter a clear role name for the signer (e.g. Client, HR Manager, Witness).'
                : 'Enter a descriptive label for this field (e.g. Contract Date, Company Name).'}
              </p>
              <input
                className="field-modal-input"
                placeholder={fieldModal.type === 'role' ? 'e.g. Client' : 'e.g. Contract Date'}
                value={fieldModal.label}
                onChange={e => setFieldModal({ ...fieldModal, label: e.target.value })}
                onKeyDown={e => {
                  if (e.key === 'Enter') {
                    e.preventDefault();
                    applyFieldModal();
                  }
                }}
                autoFocus
              />
              <div className="field-modal-actions">
                <button
                  type="button"
                  className="btn btn-secondary"
                  onClick={() => setFieldModal({ open: false, type: null, label: "" })}
                >
                  Cancel
                </button>
                <button
                  type="button"
                  className="btn btn-primary"
                  disabled={!fieldModal.label.trim()}
                  onClick={applyFieldModal}
                >
                  <FiCheck style={{ marginRight: 4 }} /> Add
                </button>
              </div>
            </div>
          </div>
        )}
        </div>
    );
}

export default CreateTemplate;